import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import SmartStaysLogo from './SmartStaysLogo';
import LanguageSwitcher from './LanguageSwitcher';
import FAQ_CATEGORY_SLUGS from '../faqCategorySlugs';
import { getAuthToken } from '../utils/authSession';
import './Navbar.css';
import '../publicTheme.css';

function Navbar() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const isNative = localStorage.getItem('isNativeApp') === 'true';
  const isAuthenticated = getAuthToken() !== null;
  const [menuOpen, setMenuOpen] = useState(false);
  // Op mobiel staan Aanbod/FAQ standaard ingeklapt - dit houdt bij welke van
  // de twee (indien enig) uitgeklapt is ('pricing' | 'faq' | null). Op
  // desktop heeft dit geen effect, daar blijft alles op :hover werken.
  const [openDropdown, setOpenDropdown] = useState(null);

  // Sluit het mobiele menu automatisch bij elke navigatie.
  useEffect(() => {
    setMenuOpen(false);
    setOpenDropdown(null);
  }, [location.pathname]);

  // Niet tonen in de mobiele app (die heeft geen publieke marketingpagina's).
  // Bewust WEL tonen als de gebruiker ingelogd is: Navbar wordt enkel
  // gerenderd op de publieke pagina's (Home, Aanbod, Over ons, FAQ, Contact,
  // Download) - /admin, /dashboard en /cleaner hebben hun eigen nav en
  // gebruiken dit component niet. Een ingelogde admin/verhuurder/schoonmaker
  // die toch naar smartstays.be navigeert (bv. gewoon het domein opnieuw
  // bezoeken) zag hierdoor voorheen HELEMAAL geen navbar meer - geen manier
  // om terug naar het eigen dashboard te navigeren.
  if (isNative) {
    return null;
  }

  const goToLogin = () => {
    // Login.js herkent zelf al een bestaand token en stuurt dan meteen door
    // naar het juiste dashboard (admin/verhuurder/schoonmaker) - dus deze
    // knop werkt ook correct wanneer je al ingelogd bent.
    navigate('/login');
    setMenuOpen(false);
  };

  // Bepaal welke link actief is
  const isActive = (path) => location.pathname === path;
  const closeMenu = () => {
    setMenuOpen(false);
    setOpenDropdown(null);
  };
  const toggleDropdown = (key) => {
    setOpenDropdown((prev) => (prev === key ? null : key));
  };

  return (
    <nav className="navbar">
      <div className="navbar-container">
        <Link to="/" className="navbar-brand" onClick={closeMenu}>
          <SmartStaysLogo className="nav-logo" />
        </Link>
        <div className="navbar-mobile-controls">
          <button onClick={goToLogin} className="navbar-login-btn navbar-login-btn-mobile">
            {t('nav.login')}
          </button>
          <button
            type="button"
            className={`navbar-toggle ${menuOpen ? 'open' : ''}`}
            onClick={() => setMenuOpen(!menuOpen)}
            aria-label={t('nav.toggleMenu')}
            aria-expanded={menuOpen}
          >
            <span></span>
            <span></span>
            <span></span>
          </button>
        </div>
        <div className={`navbar-menu ${menuOpen ? 'open' : ''}`}>
          <div className="navbar-links">
            <Link to="/" className={`navbar-link ${isActive('/') ? 'active' : ''}`} onClick={closeMenu}>
              {t('nav.home')}
            </Link>
            <div className="navbar-dropdown">
              <div className="navbar-dropdown-head">
                <Link to="/selection" className={`navbar-link ${isActive('/selection') ? 'active' : ''}`} onClick={closeMenu}>
                  {t('nav.pricing')}
                </Link>
                <button
                  type="button"
                  className="dropdown-toggle-btn"
                  onClick={() => toggleDropdown('pricing')}
                  aria-label={t('nav.pricing')}
                  aria-expanded={openDropdown === 'pricing'}
                >
                  <svg className={`dropdown-chevron ${openDropdown === 'pricing' ? 'open' : ''}`} width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><path d="M6 9l6 6 6-6" /></svg>
                </button>
              </div>
              <div className={`navbar-dropdown-menu ${openDropdown === 'pricing' ? 'mobile-open' : ''}`}>
                <Link to="/selection?tab=toegangsbeheer" onClick={closeMenu}>{t('pricing.tabs.accessManagement')}</Link>
                <Link to="/selection?tab=schoonmaak" onClick={closeMenu}>{t('pricing.tabs.cleaning')}</Link>
                <Link to="/selection?tab=automation" onClick={closeMenu}>{t('pricing.tabs.automation')}</Link>
              </div>
            </div>
            <Link to="/about" className={`navbar-link ${isActive('/about') ? 'active' : ''}`} onClick={closeMenu}>
              {t('nav.about')}
            </Link>
            <div className="navbar-dropdown">
              <div className="navbar-dropdown-head">
                <Link to="/faq" className={`navbar-link ${isActive('/faq') ? 'active' : ''}`} onClick={closeMenu}>
                  {t('nav.faq')}
                </Link>
                <button
                  type="button"
                  className="dropdown-toggle-btn"
                  onClick={() => toggleDropdown('faq')}
                  aria-label={t('nav.faq')}
                  aria-expanded={openDropdown === 'faq'}
                >
                  <svg className={`dropdown-chevron ${openDropdown === 'faq' ? 'open' : ''}`} width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><path d="M6 9l6 6 6-6" /></svg>
                </button>
              </div>
              <div className={`navbar-dropdown-menu ${openDropdown === 'faq' ? 'mobile-open' : ''}`}>
                {t('faq.categories', { returnObjects: true }).map((category, i) => (
                  <Link key={FAQ_CATEGORY_SLUGS[i]} to={`/faq#${FAQ_CATEGORY_SLUGS[i]}`} onClick={closeMenu}>
                    {category.title}
                  </Link>
                ))}
              </div>
            </div>
            <Link to="/contact" className={`navbar-link ${isActive('/contact') ? 'active' : ''}`} onClick={closeMenu}>
              {t('nav.contact')}
            </Link>
            <Link to="/download" className={`navbar-link ${isActive('/download') ? 'active' : ''}`} onClick={closeMenu}>
              {t('nav.download')}
            </Link>
          </div>
          <div className="navbar-right">
            <LanguageSwitcher />
            <button onClick={goToLogin} className="navbar-login-btn">{t('nav.login')}</button>
          </div>
        </div>
      </div>
    </nav>
  );
}

export default Navbar;
